import { Chip, StatusDot, StatusPill } from "@/components/iris";
import type { StatusDotVariant } from "@/components/iris";
import { AlertHistory } from "@/features/alerts/alert-history";
import {
  CNC_LABEL,
  fireCountText,
  lastSeenText,
  splitProblems,
} from "@/features/alerts/alert-problem";
import { NotificationSettings } from "@/features/alerts/notification-settings";
import { RuleEditor } from "@/features/alerts/rule-editor";
import { useAcknowledgeAlert, useActiveAlerts, useSilenceAlert } from "@/hooks/use-alerts";
import type { AlertInstance, Env } from "@/lib/types";
import { useState } from "react";

type EnvFilter = "all" | Env | "scraping";
type AlertTab = "Active Alerts" | "Alert History" | "Rules" | "Notification Settings";

const TABS: AlertTab[] = ["Active Alerts", "Alert History", "Rules", "Notification Settings"];

const ENV_FILTERS: { label: string; value: EnvFilter }[] = [
  { label: "All", value: "all" },
  { label: "Dev", value: "dev" },
  { label: "Prod", value: "prod" },
  { label: "Scraping", value: "scraping" },
];

const SEV_VARIANT: Record<string, StatusDotVariant> = {
  critical: "err",
  warning: "warn",
  info: "idle",
  could_not_check: "idle",
};

// IR-1788 r14: an alert's environment is the environment of the backend that served it
// (each environment's alerts live in that environment's database, and this build's API
// is same-origin), never a guess from the `source` text. If the build does not say, the
// environment is "unknown" and is never defaulted to dev.
function servedEnv(): Env | "unknown" {
  const v = import.meta.env.VITE_IRIS_ENV;
  return v === "prod" || v === "dev" ? v : "unknown";
}

function inferEnv(source: string): Env | "scraping" | "unknown" {
  const s = source.toLowerCase();
  if (s.includes("scraper") || s.includes("scraping") || s.includes("vm")) return "scraping";
  return servedEnv();
}

function EnvTag({ env }: { env: Env | "scraping" | "unknown" }) {
  if (env === "scraping") {
    return <StatusPill variant="warn">scraping</StatusPill>;
  }
  if (env === "unknown") {
    return <StatusPill variant="idle">unknown</StatusPill>;
  }
  return <StatusPill variant={env === "prod" ? "inactive" : "ok"}>{env}</StatusPill>;
}

function AlertsWithEnvFilter() {
  const { data: alerts, isLoading } = useActiveAlerts();
  const ack = useAcknowledgeAlert();
  const silence = useSilenceAlert();
  const [envFilter, setEnvFilter] = useState<EnvFilter>("all");

  const filtered = (alerts ?? []).filter((a) => {
    if (envFilter === "all") return true;
    return inferEnv(a.source) === envFilter;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--iris-s-4)" }}>
      {/* Filter chips */}
      <div style={{ display: "flex", gap: 8 }}>
        {ENV_FILTERS.map(({ label, value }) => (
          <Chip key={value} active={envFilter === value} onClick={() => setEnvFilter(value)}>
            {label}
          </Chip>
        ))}
      </div>

      {/* Table */}
      <div className="iris-card" style={{ overflow: "hidden" }}>
        <table className="iris-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>Sev</th>
              <th>Alert</th>
              <th>Source</th>
              <th style={{ width: 64 }}>Env</th>
              <th style={{ width: 140 }}>Fired</th>
              <th style={{ width: 170 }}>Last seen</th>
              <th style={{ width: 80 }}>Since</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", color: "var(--iris-fg-4)", padding: "16px 12px" }}
                >
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", color: "var(--iris-fg-4)", padding: "16px 12px" }}
                >
                  No active alerts
                </td>
              </tr>
            ) : (
              (() => {
                const { problems, couldNotCheck } = splitProblems(filtered);
                const renderRow = (a: AlertInstance, kind: "alert" | "could_not_check") => {
                  const env = inferEnv(a.source);
                  const fired = fireCountText(a);
                  const seen = lastSeenText(a);
                  return (
                    <tr key={a.id} data-alert-row data-alert-kind={kind}>
                      <td>
                        <StatusDot variant={SEV_VARIANT[a.severity] ?? "idle"} size={10} />
                      </td>
                      <td
                        style={{ color: "var(--iris-fg-1)", overflowWrap: "anywhere" }}
                        title={a.message}
                      >
                        {a.message}
                      </td>
                      <td
                        className="iris-td--muted"
                        style={{ overflowWrap: "anywhere" }}
                        title={a.source}
                      >
                        {a.source}
                      </td>
                      <td>
                        <EnvTag env={env} />
                      </td>
                      <td className="iris-td--muted" style={{ whiteSpace: "nowrap" }}>
                        {fired ?? "—"}
                      </td>
                      <td
                        className="iris-td--muted"
                        style={{ whiteSpace: "nowrap" }}
                        title={seen?.title}
                      >
                        {seen ? seen.text : "—"}
                      </td>
                      <td className="iris-td--muted">{formatAge(a.fired_at)}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                          <button
                            type="button"
                            className="iris-btn"
                            style={{ fontSize: 11, padding: "4px 10px" }}
                            onClick={() => ack.mutate(a.id)}
                            disabled={ack.isPending || a.acknowledged_at !== null}
                          >
                            Ack
                          </button>
                          <button
                            type="button"
                            className="iris-btn"
                            style={{ fontSize: 11, padding: "4px 10px" }}
                            onClick={() => silence.mutate({ alertId: a.id, duration_minutes: 60 })}
                            disabled={silence.isPending}
                          >
                            Silence
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                };
                return (
                  <>
                    {problems.map((a) => renderRow(a, "alert"))}
                    {couldNotCheck.length > 0 && (
                      <tr data-testid="alerts-cnc-section">
                        <td
                          colSpan={8}
                          style={{
                            color: "var(--iris-fg-3)",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "8px 12px",
                          }}
                        >
                          {CNC_LABEL}
                        </td>
                      </tr>
                    )}
                    {couldNotCheck.map((a) => renderRow(a, "could_not_check"))}
                  </>
                );
              })()
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AlertsTab() {
  const [tab, setTab] = useState<AlertTab>("Active Alerts");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--iris-s-4)" }}>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 8 }}>
        {TABS.map((t) => (
          <Chip key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </Chip>
        ))}
      </div>

      {tab === "Active Alerts" && <AlertsWithEnvFilter />}
      {tab === "Alert History" && <AlertHistory />}
      {tab === "Rules" && <RuleEditor />}
      {tab === "Notification Settings" && <NotificationSettings />}
    </div>
  );
}

function formatAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}
